import React from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { PageHeader } from '@/components/layout'
import { useAuth } from '@/hooks/useAuth'
import { useMyCommissions as useCommissions } from '@/hooks/useCommission'

export const AffiliateEarningsPage: React.FC = () => {
    const { t } = useTranslation('common')
    const { user } = useAuth()
    const { data: commissions } = useCommissions(user?.id || '')

    const totalEarned = commissions?.filter((c: any) => c.status === 'paid')?.reduce((acc: number, c: any) => acc + (c.amount || 0), 0) || 0
    const pendingEarnings = commissions?.filter((c: any) => c.status === 'pending')?.reduce((acc: number, c: any) => acc + (c.amount || 0), 0) || 0
    const availableBalance = commissions?.filter((c: any) => c.status === 'earned')?.reduce((acc: number, c: any) => acc + (c.amount || 0), 0) || 0

    const getStatusBadgeColor = (status: string) => {
        switch (status) {
            case 'pending': return 'bg-yellow-500/20 text-yellow-400'
            case 'earned': return 'bg-blue-500/20 text-blue-400'
            case 'paid': return 'bg-green-500/20 text-green-400'
            default: return 'bg-gray-500/20 text-gray-500 dark:text-gray-400'
        }
    }

    return (
        <div>
            <PageHeader
                title="Earnings"
                subtitle="Track your affiliate commissions"
            />

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                <div className="rounded-lg bg-white dark:bg-surface-card border border-gray-200 dark:border-white/5 p-5">
                    <div className="flex items-center gap-2 mb-2">
                        <span className="material-symbols-outlined text-green-400 text-[20px]">account_balance_wallet</span>
                        <p className="text-gray-500 dark:text-text-muted text-xs font-medium uppercase">Available Balance</p>
                    </div>
                    <p className="text-red-400 text-3xl font-bold">Rp {availableBalance.toLocaleString('id-ID')}</p>
                    <Link
                        to="/affiliate/withdraw"
                        className="mt-4 w-full py-2.5 bg-primary text-white rounded-lg text-sm font-bold hover:scale-105 transition-transform flex items-center justify-center"
                    >
                        Request Withdrawal
                    </Link>
                </div>
                <div className="rounded-lg bg-white dark:bg-surface-card border border-gray-200 dark:border-white/5 p-5">
                    <div className="flex items-center gap-2 mb-2">
                        <span className="material-symbols-outlined text-yellow-400 text-[20px]">schedule</span>
                        <p className="text-gray-500 dark:text-text-muted text-xs font-medium uppercase">Pending (30-day lock)</p>
                    </div>
                    <p className="text-gray-900 dark:text-white text-3xl font-bold">Rp {pendingEarnings.toLocaleString('id-ID')}</p>
                    <p className="mt-4 text-xs text-gray-500 dark:text-text-muted">Locked until refund period ends</p>
                </div>
                <div className="rounded-lg bg-white dark:bg-surface-card border border-gray-200 dark:border-white/5 p-5">
                    <div className="flex items-center gap-2 mb-2">
                        <span className="material-symbols-outlined text-blue-400 text-[20px]">trending_up</span>
                        <p className="text-gray-500 dark:text-text-muted text-xs font-medium uppercase">Total Earned (All Time)</p>
                    </div>
                    <p className="text-gray-900 dark:text-white text-3xl font-bold">Rp {totalEarned.toLocaleString('id-ID')}</p>
                    <p className="mt-4 text-xs text-gray-500 dark:text-text-muted">Since you joined</p>
                </div>
            </div>

            {/* Commission History */}
            <div className="rounded-lg bg-white dark:bg-surface-card border border-gray-200 dark:border-white/5 overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-200 dark:border-white/5">
                    <h3 className="font-bold text-gray-900 dark:text-white">Commission History</h3>
                </div>
                <table className="w-full">
                    <thead>
                        <tr className="border-b border-gray-200 dark:border-white/5">
                            <th className="text-left px-5 py-4 text-xs font-bold uppercase text-gray-500 dark:text-text-muted">Course</th>
                            <th className="text-left px-5 py-4 text-xs font-bold uppercase text-gray-500 dark:text-text-muted">{t('role.participant')}</th>
                            <th className="text-left px-5 py-4 text-xs font-bold uppercase text-gray-500 dark:text-text-muted">Amount</th>
                            <th className="text-left px-5 py-4 text-xs font-bold uppercase text-gray-500 dark:text-text-muted">Status</th>
                            <th className="text-left px-5 py-4 text-xs font-bold uppercase text-gray-500 dark:text-text-muted">Date</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-white/5">
                        {commissions?.length === 0 ? (
                            <tr><td colSpan={5} className="px-5 py-12 text-center text-gray-500 dark:text-text-muted">No commissions yet</td></tr>
                        ) : (
                            // Show only the 15 most recent commissions for dashboard preview
                            commissions?.slice(0, 15).map((c: any) => (
                                <tr key={c.id} className="hover:bg-gray-100 dark:hover:bg-white/5 transition-colors">
                                    <td className="px-5 py-4 text-sm text-gray-900 dark:text-white">{c.courseName || 'Course'}</td>
                                    <td className="px-5 py-4 text-sm text-gray-500 dark:text-text-muted">{c.studentName || t('role.participant')}</td>
                                    <td className="px-5 py-4 text-sm font-bold text-red-400">Rp {(c.amount || 0).toLocaleString('id-ID')}</td>
                                    <td className="px-5 py-4">
                                        <span className={`inline-flex px-2.5 py-1 rounded-full text-[10px] font-bold uppercase ${getStatusBadgeColor(c.status)}`}>
                                            {c.status}
                                        </span>
                                    </td>
                                    <td className="px-5 py-4 text-sm text-gray-500 dark:text-text-muted">{c.createdAt ? new Date(c.createdAt).toLocaleDateString('id-ID') : '-'}</td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    )
}

export default AffiliateEarningsPage
